import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/Button";
import Navbar from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import {
  AlignCenterVertical,
  Award,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  FlaskConical,
  LibraryBig,
  Minus,
  Plus,
  Smartphone,
} from "lucide-react";
import { useEffect, useState } from "react";

const items = [
  {
    title: "Create A Store in Minutes",
    description:
      "Stop losing customers due to WhatsApp and Instagram limitations. Our guided setup asks simple questions about your business and automatically creates your perfect store - whether you're selling fashion, beauty products, food, or digital services.",
  },
  {
    title: "Business Protection",
    description:
      "Sleep easy with enterprise-grade security, automatic backups, and fraud prevention. We handle the technical safeguards so you can focus on growing your business risk-free.",
  },
  {
    title: "Mobile-First Design",
    description:
      "Over 80% of your customers shop on phones. Our stores look stunning on any device, with lightning-fast loading times that keep mobile shoppers engaged and buying.",
  },
  {
    title: "Never Miss a Sale Again",
    description:
      "24/7 automated storefront means your business never sleeps. Capture orders while you're offline with integrated payments, cart recovery, and instant order notifications.",
  },
  {
    title: "See Who's Looking",
    description:
      "Get real-time insights about your visitors - see what products they view, how long they stay, and what makes them buy. Turn anonymous traffic into known customers.",
  },
];

const featureItems = [
  {
    icon: <AlignCenterVertical className="rotate-180" color="#7E57C2" />,
    title: "Empathy-Driven Approach",
    description:
      "We design with empathy. We understand our users' struggles, goals, and needs, and create solutions that cater to their unique challenges and aspirations.",
  },
  {
    icon: <Award color="#7E57C2" />,
    title: "Earn customer trust",
    description:
      "Our websites come with integrated payment services like Paystack which is secure and highly trusted by customers globally. Instantly look more legit!",
  },
  {
    icon: <LibraryBig color="#7E57C2" />,
    title: "Keep Customers updated",
    description:
      "Our websites allow you to upload and showcase all your products, so your customers will be informed on what products are available at a glance.",
  },
  {
    icon: <CreditCard color="#7E57C2" />,
    title: "Make sales in your sleep",
    description:
      "With fully automated payment systems, customers are able to make purchases from your website even while you're offline.",
  },
];

const slides = [
  {
    title: "Store Essentials",
    items: [
      "Beautiful responsive templates that look great on all devices",
      "Unlimited product listings with photos and videos",
      "Custom domain integration (.com, .com.ng, or free .jellosite.store)",
      "Dynamic delivery pricing options for physical products",
      "99.9% uptime for reliable 24/7 sales",
    ],
  },
  {
    title: "Marketing & Analytics",
    items: [
      "Facebook Pixel integration for ad campaign tracking",
      "Built-in visitor statistics to monitor store traffic",
      "Product performance tracking to optimize your offerings",
      "SEO-friendly pages to help customers find you online",
    ],
  },
  {
    title: "Payment Solutions",
    items: [
      "Seamless Paystack integration",
      "Flutterwave payment processing",
      "Automated payment confirmation",
      "Secure checkout process that builds customer trust",
    ],
  },
  {
    title: "Communication Tools",
    items: [
      "WhatsApp integration (Coming soon!)",
      "Automatic order notifications",
      "Customer account creation",
      "Product wishlist and favorites",
    ],
  },
  {
    title: "Smart Business Tools",
    items: [
      "Guided store setup based on your business type",
      "Helpful tooltips throughout the dashboard",
      "Mobile-friendly admin interface to manage on the go",
      "Simple inventory management",
    ],
  },
];

const LandingPage = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);

  const iconVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const listContainerVariants = {
    hidden: { opacity: 1 },
    visible: {
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren",
      },
    },
  };

  const listItemVariants = {
    hidden: { opacity: 1 },
    visible: { opacity: 1 },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const slideVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.09,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { x: -200, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 10,
        bounce: 0.5,
      },
    },
  };

  const listItemVariant = {
    hidden: (i: number) => ({
      x: 100 + i * 50, // Staggered starting position
      opacity: 0,
    }),
    visible: (i: number) => ({
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 10,
        bounce: 0.3,
        delay: i * 0.1,
      },
    }),
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleNext = () => {
    setSlideIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setSlideIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  return (
    <motion.div>
      <Navbar />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="landing-bg pb-10 bg-cover bg-no-repeat bg-[center_-1000px] overflow-hidden sm:bg-[center_-750px]"
      >
        <div className="px-4 md:px-8 ">
          <motion.div className="flex  flex-col gap-7 items-center justify-center text-center py-12">
            <motion.div variants={itemVariants} className="flex flex-col gap-3">
              <motion.h1 className=" responsive-font-h1 font-medium leading-9 md:leading-13">
                <h1 className=" responsive-font-h1 font-medium text-purple-400">
                  Create your store.
                </h1>
                Make more sales. Scale your business.{" "}
              </motion.h1>
              <span className="responsive-font-p">
                <span className=" responsive-font-p font-medium text-purple-500">
                  JELLOSITE
                </span>{" "}
                helps business owners take their business online, reach more{" "}
                <br className="hidden md:block" /> customers and make more
                sales. Create a{" "}
                <span className=" responsive-font-p font-medium text-purple-500">
                  FREE{" "}
                </span>
                store now.{" "}
              </span>
            </motion.div>
            <Link to="signup" className="w-full">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              >
                <Button
                  text="Create your store"
                  className="w-full sm:w-fit bg-[#945bf5] rounded-[40px] transition-all duration-300 ease-in-out hover:bg-white hover:text-[#945bf5]  hover:shadow-[0_0_0_2px_#945bf5]"
                />
              </motion.div>
            </Link>
            <motion.ul
              variants={containerVariants}
              className="flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-center list-disc md:list-none"
            >
              <li className="text-[#5B5B5B] responsive-font-li">
                Not Technical Knowledge Needed
              </li>
              <hr className=" hidden md:block rotate-90 w-6 h-1 text-[#5B5B5B]" />
              <li className="text-[#5B5B5B] responsive-font-li">
                Works For All Businesses
              </li>
              <hr className=" hidden md:block rotate-90 w-6 h-1 text-[#5B5B5B]" />
              <li className="text-[#5B5B5B] responsive-font-li">Quick Setup</li>
            </motion.ul>
            <motion.img
              src="/assets/Desktop.png"
              alt=""
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            />
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={slideVariants}
              className=" responsive-font-h2 font-medium mt-10"
            >
              Why Choose Jellosite?
            </motion.h2>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
            variants={listContainerVariants}
          >
            <motion.ul
              variants={listContainerVariants}
              className="flex items-start justify-center w-full gap-10 sm:gap-20 flex-wrap py-8 border-t border-b border-t-[#E2E8F0] border-b-[#E2E8F0]"
            >
              {featureItems.map((item, index) => (
                <motion.li
                  key={index}
                  variants={listItemVariants}
                  className="w-[270px] flex flex-col items-center justify-center text-center gap-2"
                >
                  <motion.div
                    variants={iconVariants}
                    className="h-13 w-13 bg-[#EDE9FE] rounded-full flex items-center justify-center"
                  >
                    {item.icon}
                  </motion.div>
                  <motion.div variants={contentVariants}>
                    <h4 className="font-medium">{item.title}</h4>
                    <span className="text-[#626262]">{item.description}</span>
                  </motion.div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <div className="flex items-center flex-col justify-center gap-10 my-20">
            <h2 className="font-medium">Built for Entrepreneurs</h2>
            <div className="flex items-center md:items-start flex-col gap-20 md:gap-20 md:flex-row justify-center">
              {/* Animated Image */}
              <motion.div
                className="md:sticky md:top-20 h-fit"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                variants={imageVariants}
              >
                <motion.img
                  src="/assets/Left@2x.png"
                  alt=""
                  className="w-[300px] sm:w-[500px] md:min-h-[400px] object-contain transition-opacity duration-300 hover:opacity-95"
                  whileHover={{ scale: 1.05 }}
                />
              </motion.div>

              {/* Animated List */}
              <motion.ul
                className="space-y-2 flex items-center justify-start relative md:top-15 flex-col w-full max-h-[400px] md:max-w-[600px]"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              >
                {items.map((item, index) => (
                  <motion.li
                    key={index}
                    custom={index}
                    variants={listItemVariant}
                    className="group relative transition-all duration-300 cursor-pointer"
                    style={{ minHeight: "3rem" }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className="flex items-start">
                      <hr
                        className={`h-full w-[2px] border-0 bg-[#CBD5E1] group-hover:bg-[#7E57C2] absolute left-2 -translate-x-1/2 transition-all duration-300 origin-bottom opacity-100 
                ${activeIndex === index ? "!bg-[#7E57C2]" : ""}`}
                      />
                      <div className="pl-8 w-full">
                        <div
                          className={`group-hover:bg-[#F9F5FF] p-1 rounded-lg 
                  ${activeIndex === index ? "!bg-[#F9F5FF]" : ""}`}
                        >
                          <h5 className="font-medium transition-colors duration-300">
                            {item.title}
                          </h5>
                          <div
                            className={`overflow-hidden transition-all duration-300 
                    ${activeIndex === index ? "max-h-[300px]" : "max-h-0"} 
                    group-hover:max-h-[300px]`}
                          >
                            <p
                              className={`pt-2 text-slate-500 opacity-0 transition-opacity duration-300 
                      group-hover:opacity-100 
                      ${activeIndex === index ? "!opacity-100" : ""}`}
                            >
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
          <div className="my-25 flex items-center justify-center flex-col gap-10">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={itemVariants}
              className=" font-medium text-center leading-9"
            >
              Here’s how JELLOSITE creates a store for{" "}
              <br className="hidden sm:block" /> YOU in 3 easy steps{" "}
            </motion.h2>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              className="flex items-center flex-col-reverse md:flex-row justify-center gap-8"
            >
              <ul className="flex items-start justify-center gap-9 flex-col">
                <li className=" flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 sm:gap-6 w-full">
                  <motion.div
                    variants={itemVariants}
                    className="w-12 h-12 rounded-full bg-[#008EFF] flex justify-center items-center text-white aspect-square shrink-0"
                  >
                    <FileText />
                  </motion.div>
                  <div className="flex items-start justify-center text-center flex-col gap-1 w-full">
                    <motion.h4
                      variants={itemVariants}
                      className="font-medium w-full sm:w-fit"
                    >
                      Simply Copy & Paste
                    </motion.h4>
                    <motion.span
                      variants={itemVariants}
                      className="text-[#12141D] w-full sm:w-fit"
                    >
                      Use limited-time offers or exclusive deals to prompt
                      immediate action.
                    </motion.span>
                  </div>
                </li>
                <li className="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 sm:gap-6 w-full">
                  <motion.div
                    variants={itemVariants}
                    className="w-12 h-12 rounded-full bg-[#FABB18] flex justify-center items-center text-white aspect-square shrink-0"
                  >
                    <Smartphone />
                  </motion.div>
                  <div className="flex items-start justify-center text-center sm:text-left flex-col gap-1 w-full">
                    <motion.h4
                      variants={itemVariants}
                      className="font-medium w-full sm:w-fit"
                    >
                      Optimize for Mobile
                    </motion.h4>
                    <motion.span
                      variants={itemVariants}
                      className="text-[#12141D] w-full sm:w-fit"
                    >
                      Ensure emails are mobile-friendly to reach customers
                      on-the-go.
                    </motion.span>
                  </div>
                </li>
                <li className=" flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4 sm:gap-6 w-full">
                  <motion.div
                    variants={itemVariants}
                    className="w-12 h-12 rounded-full bg-[#2F872D] flex justify-center items-center text-white aspect-square shrink-0"
                  >
                    <FlaskConical />
                  </motion.div>
                  <div className="flex items-start justify-center text-center flex-col gap-1 w-full ">
                    <motion.h4
                      variants={itemVariants}
                      className="font-medium w-full sm:w-fit"
                    >
                      Test and Iterate
                    </motion.h4>
                    <motion.span
                      variants={itemVariants}
                      className="text-[#12141D] w-full sm:w-fit"
                    >
                      Continuously test different strategies and analyze results
                      to refine your approach.
                    </motion.span>
                  </div>
                </li>
              </ul>
              <motion.img
                variants={imageVariants}
                src="/assets/Phonegroup.png"
                className="w-[300px] sm:w-[400px]"
                alt=""
              />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "0px 0px -100px 0px" }}
          variants={containerVariants}
          className="py-10 bg-[#FEFFF1]"
        >
          <motion.h2
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
            className="text-center font-medium leading-9 px-3"
          >
            Powerful Features in Every Jellosite{" "}
            <br className="hidden sm:block" />
            Store
          </motion.h2>
          <div className=" relative py-12 flex flex-col md:flex-row items-center justify-center gap-11 md:gap-20">
            <img
              src="/assets/sectionimage.png"
              alt=""
              className="w-[300px] sm:w-[400px]"
            />
            <div className="flex items-center md:items-start gap-2 flex-col justify-center px-6 w-full sm:w-fit">
              <h3 className="font-medium">{slides[slideIndex].title}</h3>
              <ul className="list-disc flex flex-col items-start sm:w-fit w-full  justify-center gap-2">
                {slides[slideIndex].items.map((item, index) => (
                  <li key={index} className="text-[#5C5C5C] sm:w-fit w-full ">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className=" absolute flex mt-10 sm:mt-5 md:mt-0 h-full items-end  sm:items-center justify-between w-full px-10">
              <ChevronLeft
                size={40}
                cursor="pointer"
                onClick={handlePrev}
                className="p-2 rounded-full bg-white shadow-lg hover:bg-gray-100 transition-color"
              />
              <ChevronRight
                size={40}
                cursor="pointer"
                onClick={handleNext}
                className="p-2 rounded-full bg-white shadow-lg hover:bg-gray-100 transition-color"
              />
            </div>
          </div>
          <div className="flex gap-2 items-center justify-center md:mt-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setSlideIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                  slideIndex === index ? "bg-[#7E57C2]" : "bg-[#CBD5E1]"
                }`}
              />
            ))}
          </div>
        </motion.div>
        <div className="bg-[#4C1D95] py-16 flex flex-col items-center justify-center gap-5 landing-pricing bg-no-repeat bg-left">
          <div className="flex flex-col items-center justify-center">
            <h5 className="text-[#A78BFA]">PRICING</h5>
            <h2 className="font-bold text-white text-center">
              Simple Pricing for your business
            </h2>
          </div>
          <p className="text-white font-light -mt-2">
            Super Charge features for just 5k/mo
          </p>
          <Link to="signup">
            <Button
              text=" Start for Free"
              className="px-6 py-3 rounded-lg primary-btn w-fit cursor-pointer text-white font-bold shadow-lg"
            ></Button>
          </Link>
        </div>
        <div className="px-4 md:px-8">
          <div className="py-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              animate="visible"
              className="flex items-center justify-center gap-8 flex-col"
            >
              <div className="flex items-center justify-center text-center flex-col">
                <motion.h2
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                  variants={contentVariants}
                >
                  Frequently Asked Questions
                </motion.h2>
                <motion.span
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                  variants={contentVariants}
                  className="text-[#707070]"
                >
                  Find questions and answers related to your problems
                </motion.span>
              </div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                variants={contentVariants}
                className="flex items-center justify-center gap-6 flex-col w-full min-w-[280px] max-w-[770px]"
              >
                <FAQItem
                  question="Do I need technical skills to use JelloSite?"
                  answer={
                    <>
                      No you do not need any technical skills, it's easy to
                      navigate and self explanatory. But if you find yourself
                      needing help, you can use the{" "}
                      <Link to="" className="underline text-[#7E57C2]">
                        resource
                      </Link>
                      .
                    </>
                  }
                />
                <FAQItem
                  question="How long does it take to set up my store?"
                  answer="Typically takes less than 5 minutes to get started with basic setup."
                />
                <FAQItem
                  question="What payment methods do you support?"
                  answer="We support all major payment gateways including Paystack and Flutterwave."
                />
                <FAQItem
                  question="Can I use my own domain name?"
                  answer="Yes, you can easily connect your custom domain in your dashboard settings."
                />
                <FAQItem
                  question="Is there a mobile app?"
                  answer="Our platform is fully responsive and works great on any mobile browser."
                />
                <FAQItem
                  question="Can I sell digital products?"
                  answer="Yes! Our e-commerce platform supports the sales of digital products"
                />
              </motion.div>
            </motion.div>
          </div>
          <div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={contentVariants}
              className="mb-15"
            >
              <motion.h2
                variants={contentVariants}
                className="font-medium flex items-center gap-1 justify-center py-15"
              >
                Stop Chatting.<h2 className="text-[#945BF5]">Start Selling</h2>
              </motion.h2>
              <motion.div className="flex items-center md:items-start justify-center flex-col md:flex-row gap-20">
                <div className="flex items-start justify-center gap-3">
                  <motion.ul
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="flex flex-col gap-3 items-center justify-center list-disc pl-4 pr-2"
                  >
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-b border-b-[#E4E4E4] w-full"
                    >
                      Every day your business operates only on WhatsApp or
                      Instagram is another day of missed opportunities.
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] w-full"
                    >
                      While you're manually answering the same product
                      questions... While you're waiting for payment
                      screenshots... While your store is "closed" because you're
                      sleeping...
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-t border-t-[#E4E4E4] w-full"
                    >
                      Your competitors with websites are making sales
                      automatically.
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-t border-t-[#E4E4E4] w-full"
                    >
                      The difference between a struggling social media business
                      and a thriving brand isn't luck. It's having professional
                      tools that work for you 24/7.
                    </motion.li>
                    <motion.li
                      initial="hidden"
                      animate="visible"
                      variants={contentVariants}
                      className="py-2 text-[#333333CC] border-t border-t-[#E4E4E4] w-full"
                    >
                      For less than the cost of your data plan, you can
                      transform your business today.
                    </motion.li>
                  </motion.ul>
                </div>
                <img
                  src="/assets/stopchatting.png"
                  className="sm:w-[450px] w-full"
                  alt=""
                />
              </motion.div>
            </motion.div>
            <div className="flex items-center justify-center flex-col gap-4 mt-15 md:mt-20">
              <Link to="signup">
                <Button
                  text="Start for free"
                  className="w-full sm:w-fit bg-[#945bf5] rounded-[40px] transition-all duration-300 ease-in-out hover:bg-white hover:text-[#945bf5]  hover:shadow-[0_0_0_2px_#945bf5]"
                />
              </Link>
              <p className=" text-[#333333CC]">
                Five minutes to set up. Zero technical skills needed.
              </p>
              <h3 className=" font-medium text-center">
                Join 100+ Nigerian entrepreneurs already growing with Jellosite
              </h3>
            </div>
          </div>
        </div>
      </motion.div>
      <Footer />
    </motion.div>
  );
};

export default LandingPage;

const FAQItem = ({
  question,
  answer,
}: {
  question: string;
  answer: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex items-start justify-between bg-[#33333308] p-7 rounded-3xl w-full">
      <div className="flex flex-col gap-1 w-full justify-center items-start">
        <h5 className="font-medium text-[#333333CC]">{question}</h5>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <p className="text-[#33333380] pt-2">{answer}</p>
        </div>
      </div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-[#333333CC] hover:text-[#7E57C2] transition-colors cursor-pointer"
      >
        {isOpen ? <Minus size={18} /> : <Plus size={16} />}
      </button>
    </div>
  );
};
